import React from 'react';
import { Shield, Check, X, Clock } from 'lucide-react';

interface VerifiedBadgeProps {
  verificationStatus: 'pending' | 'verified' | 'rejected';
  membershipTier?: 'standard' | 'premium' | null;
  showVerifiedBadge?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({
  verificationStatus,
  membershipTier,
  showVerifiedBadge = false,
  size = 'md',
  showText = false,
  className = ''
}) => {
  // Only show verified badge if user has premium membership and is verified
  const isPremiumVerified = membershipTier === 'premium' && verificationStatus === 'verified' && showVerifiedBadge;
  
  // Size configurations
  const sizeConfigs = {
    sm: {
      icon: 14,
      text: 'text-xs',
      padding: 'px-2 py-1',
      gap: 'gap-1'
    },
    md: {
      icon: 16,
      text: 'text-sm',
      padding: 'px-3 py-1.5',
      gap: 'gap-2'
    },
    lg: {
      icon: 20,
      text: 'text-base',
      padding: 'px-4 py-2',
      gap: 'gap-2'
    }
  };

  const config = sizeConfigs[size];

  // Render premium verified badge
  if (isPremiumVerified) {
    return (
      <div className={`inline-flex items-center ${config.gap} ${config.padding} bg-blue-600 text-white rounded-full font-medium ${config.text} ${className}`}>
        <Shield size={config.icon} className="fill-current" />
        {showText && <span>Verified</span>}
      </div>
    );
  }

  // Render verification status indicators (for admin/internal use)
  if (verificationStatus === 'verified') {
    return (
      <div className={`inline-flex items-center ${config.gap} ${config.padding} bg-green-100 text-green-800 rounded-full font-medium ${config.text} ${className}`}>
        <Check size={config.icon} />
        {showText && <span>Verified</span>}
      </div>
    );
  }

  if (verificationStatus === 'pending') {
    return (
      <div className={`inline-flex items-center ${config.gap} ${config.padding} bg-yellow-100 text-yellow-800 rounded-full font-medium ${config.text} ${className}`}>
        <Clock size={config.icon} />
        {showText && <span>Pending</span>}
      </div>
    );
  }

  if (verificationStatus === 'rejected') {
    return (
      <div className={`inline-flex items-center ${config.gap} ${config.padding} bg-red-100 text-red-800 rounded-full font-medium ${config.text} ${className}`}>
        <X size={config.icon} />
        {showText && <span>Rejected</span>}
      </div>
    );
  }

  return null;
};

export default VerifiedBadge; 